// ADR-weather-map-provider-2026-07-10 Phase 2 (radar map) — RainViewer's
// public, keyless frame-index endpoint + XYZ tile-URL builder
// (https://www.rainviewer.com/api.html). No API key anywhere in this
// file: every value is either a fixed public host or a field RainViewer's
// own JSON response hands back. `WeatherMapPanel` is the only consumer.

export const RAINVIEWER_MAPS_URL = "https://api.rainviewer.com/public/weather-maps.json";

// RainViewer's documented XYZ tile-URL template:
// `{host}{path}/{size}/{z}/{x}/{y}/{color}/{smooth}_{snow}.png`.
// 256px tiles; color scheme 2 ("Universal Blue" — RainViewer's own
// recommended default against a light OSM basemap); smooth=1 (smoothed
// edges); snow=1 (render snow in a distinct color rather than folding it
// into the rain palette).
const TILE_SIZE = 256;
const COLOR_SCHEME = 2;
const SMOOTH = 1;
const SHOW_SNOW_DISTINCTLY = 1;

export interface RainViewerFrame {
  time: number;
  path: string;
}

export interface RainViewerMapsResponse {
  host: string;
  radar: {
    past: RainViewerFrame[];
    nowcast?: RainViewerFrame[];
  };
}

export interface RainViewerLatestFrame {
  host: string;
  path: string;
  time: number;
}

/**
 * The single most-recent OBSERVED radar frame (`radar.past`'s last
 * entry) — deliberately not `radar.nowcast` (a short-range forecast
 * projection), matching the ADR's "latest-frame precipitation overlay"
 * (observed, not predicted). Returns `null` on any fetch/shape failure —
 * `WeatherMapPanel` treats that as "radar unavailable right now, still
 * show the base map," never a thrown error (mirrors `GET /api/v1/weather`
 * "never 5xx on upstream failure" posture, applied client-side here since
 * this is the one direct third-party fetch this app makes).
 */
export async function fetchLatestRainViewerFrame(
  fetchImpl: typeof fetch = fetch,
): Promise<RainViewerLatestFrame | null> {
  try {
    const response = await fetchImpl(RAINVIEWER_MAPS_URL);
    if (!response.ok) return null;
    const data = (await response.json()) as RainViewerMapsResponse;
    const frames = data.radar?.past ?? [];
    const latest = frames[frames.length - 1];
    if (!latest || !data.host) return null;
    return { host: data.host, path: latest.path, time: latest.time };
  } catch {
    return null;
  }
}

/**
 * Builds the Leaflet XYZ tile-URL TEMPLATE for a given frame — the
 * `{z}/{x}/{y}` placeholders are left literal for `L.tileLayer` to
 * substitute per-tile; only the frame-specific host/path/params are
 * filled in here.
 */
export function buildRainViewerTileUrlTemplate(frame: RainViewerLatestFrame): string {
  return `${frame.host}${frame.path}/${TILE_SIZE}/{z}/{x}/{y}/${COLOR_SCHEME}/${SMOOTH}_${SHOW_SNOW_DISTINCTLY}.png`;
}
